import { compileClassificationEntryFromBase } from './from-base-classification-entry';
import { createClassificationContext } from './from-base-classification-context';
import { constructInventoryDefinitionReferences, constructInventoryReferenceShell } from './from-base-inventory-references';
import { readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { constructInventoryDefinitionChoices } from './from-base-inventory-choices';
import { constructInventoryDefinitionAudio, constructInventoryDefinitionVideos } from './from-base-inventory-media';
import { finalizeInventoryDefinitionLayerSurface, inventoryDefinitionLayerOrder } from './from-base-inventory-layer-surface';
import { normalizeInventoryDefinition } from './from-base-inventory-normalization';
import { normalizeFromBaseCatalog, FROM_BASE_STATIC_REGISTRIES } from './from-base-catalog-normalization';
import { readItemField } from './item-field-reader';
import { fromBaseRecord, fromBaseUuid, freezeFromBaseRow, FromBaseStageMissing, FromBaseInputMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { randomTableRangesFromRows } from './from-base-catalog-random-tables';
import type { FromBaseInventoryMetadata } from './from-base-inventory';
import staticData from './from-base-static-registries.json';
import type { InventoryMediaMetadata } from './from-base-inventory-media';
import { constructInventoryDefinitionAssets } from './from-base-inventory-assets';
import { compilePlSignatures, createPlSignatureJsonSources } from './pl-signature';
import type { PlSignatureMetadata } from './pl-signature';
import type { InventoryVideoContext } from './from-base-inventory-media';
import { phpJsonTruthy } from './php-json-values';
import { phpJsonFloat } from './php-decimal-values';
import { projectDefinitionPendingCacheRead } from './definition-cache-surface';
import { TINY_CREATURE_SIZE_ID } from './creature-size';
import { deriveDefaultProviderNames } from './provider-catalog-order';
import { fromBaseStatusOriginNonItem } from './from-base-status-references';

export type FromBaseDefinitionMetadata = FromBaseInventoryMetadata;
/** One compilation owns standalone reads; nested ancestor expansions always rebuild. */
export interface FromBaseDefinitionCache { prepared: Map<string, Row>; served: Set<string> }

/** Do not certify omitted inputs/producers as an empty native result. The
 * constructed prefix remains inspectable while the shared K1 inputs land. */
export function fromBaseCatalogFinalizationGaps(base: Row, metadata: FromBaseDefinitionMetadata, row: Row): string[] {
    const gaps: string[] = [];
    if (!metadata.definitionAssets) gaps.push('definition-asset-inputs');
    if (!fromBaseRecord(base.definition_videos)) gaps.push('definition-video-inputs');
    const computed = fromBaseRecord(row.computed) ? row.computed : {};
    const cache = fromBaseRecord(computed._cache) ? computed._cache : {};
    if (['pl_sig_b', 'pl_sig'].some(key => !/^[0-9a-f]{40}$/.test(String(readItemField(cache, key) ?? ''))))
        gaps.push('definition-pl-signature-producer');
    if (readItemField(row, 'item_type') === 'creature' && ['skill_tier_grants_sig', 'skill_roll_sig',
        'rollup_cluster_sig', 'rollup_cluster_out', 'skills_cluster_sig'].some(key => readItemField(cache, key) === null))
        gaps.push('npc-cache-signature-producers');
    return gaps.map(gap => new FromBaseStageMissing('catalog-lean-normalization', gap).message);
}

/** Context-free post stage. Every nested definition is constructed along the
 * native ancestor chain; no oracle, cached blob or raw catalog becomes output. */
export function createFromBaseDefinitionFinalizer(base: Row, metadata: FromBaseDefinitionMetadata,
    creatureFinalizer?: (base: Row, prefix: Row) => Row, postCreature?: (host: Row, nested: boolean) => Row,
    afterPl?: (host: Row, nested: boolean) => Row, cache?: FromBaseDefinitionCache) {
    metadata = { ...metadata, plSignature: base.pl_signature as PlSignatureMetadata | undefined ?? metadata.plSignature };
    const context = createClassificationContext(base, metadata, id => definition(id, []));
    const registries: Record<string, Readonly<Record<string, Row | null>> | null> = {};
    const knownIds = [...Object.keys(fromBaseRecord(base.catalog) ? base.catalog : {}), ...Object.keys(metadata.shellRows ?? {}),
        ...base.missing_definition_ids as readonly string[] ?? []];
    const missingShells = Object.fromEntries(knownIds.map(id => [id, null]));
    const layerNames = Object.keys(metadata.layerCatalog);
    const layerOrder = metadata.definitionLayerOrder && inventoryDefinitionLayerOrder(metadata.definitionLayerOrder);
    for (const spec of Object.values(metadata.layerCatalog)) {
        const type = spec.references_item_type;
        if (!type || type in registries) continue;
        const registry = FROM_BASE_STATIC_REGISTRIES[type];
        registries[type] = registry ? { ...missingShells, ...registry } : null;
    }
    const references = { ...metadata, shellRegistries: { vitality_status: FROM_BASE_STATIC_REGISTRIES.vitality_status!,
        ...registries, ...metadata.shellRegistries } };
    const signatureJson = createPlSignatureJsonSources(fromBaseRecord(base.catalog) ? base.catalog as Record<string, Row> : {},
        metadata.definitionAssets?.vfxRows ?? [], references.shellRegistries);
    const media: InventoryMediaMetadata = { ...staticData as unknown as InventoryMediaMetadata, statusBases: {
        ...Object.fromEntries(knownIds.map(id => [id.toUpperCase(), null])), ...staticData.statusBases } };
    const active = new Set<string>();
    const prepared = cache?.prepared ?? new Map<string, Row>();
    const served = cache?.served ?? new Set<string>();
    const statusOriginNonItem = fromBaseStatusOriginNonItem(base);
    function finalize(prefix: Row, ancestors: readonly string[] = []): Row {
        // build_item_arr stamps audio before query_item_arr attaches the layers.
        const audio = constructInventoryDefinitionAudio({ items: prefix.items }, media, context.ownReaders.layerValue,
            (row, key) => context.ownReaders.layerValue(row, key) !== null || Object.keys(context.ownReaders.layerEntries(row, key)).length > 0);
        let host: Row = { ...prefix, item_layer_types: { ...prefix.item_layer_types as Row } };
        const earlyEquipment = readItemField(host, '_from_base_early_equipment_layers');
        delete host._from_base_early_equipment_layers;
        const templateOrder = readItemField(host, '_from_base_template_layer_order');
        delete host._from_base_template_layer_order;
        const id = readItemField(host, 'item_id'), raw = fromBaseUuid(id) ? context.rawDefinition(id) : null;
        // The native classification prelude runs before provider references move
        // into the bucket: an inherited size cannot suppress its own Tiny stamp.
        if (context.classificationTypes.has(String(readItemField(host, 'item_type')))
            && context.ownReaders.layerValue({ item_layer_types: host.item_layer_types }, 'size') === null)
            (host.item_layer_types as Row).size = TINY_CREATURE_SIZE_ID;
        // The prefix reader can already resolve a carrier's material. Native
        // carriers become readable after this merge window, so that inherited
        // material stays on its carrier and its object vitality follows PL.
        const inheritedMaterial = context.ownReaders.layerValue({ item_layer_types: prefix.item_layer_types }, 'item_composition') === null;
        if (inheritedMaterial) delete host.item_composition;
        if (metadata.definitionAssets) host = constructInventoryDefinitionAssets(host, context.rawDefinition,
            metadata.definitionAssets, metadata.sizeCatalog.map(row => String(readItemField(row, 'item_id'))), context.ownReaders.layerValue,
            fromBaseRecord(audio.assets) ? audio.assets : undefined);
        else if (audio.assets) host.assets = { ...host.assets as Row, ...audio.assets as Row };
        if (fromBaseRecord(base.definition_videos)) host = constructInventoryDefinitionVideos(host,
            { ...base.definition_videos as unknown as InventoryVideoContext, cdnUrl: metadata.cdnUrl! });
        // The marker is source contract metadata, not a game rule or mesh verdict.
        const computed = fromBaseRecord(host.computed) ? host.computed : {};
        host.computed = { _cache: { meshes_contract_v: 10, ...(fromBaseRecord(computed._cache) ? computed._cache : {}) },
            ...Object.fromEntries(Object.entries(computed).filter(([key]) => key !== '_cache')) };
        host = constructInventoryDefinitionChoices(host, context.readers.layerEntries, context.rawDefinition);
        // Classification queried locks/materials as standalone definitions before
        // the enclosing reference walk. Retain that child's own interned catalog.
        if (context.classificationTypes.has(String(readItemField(host, 'item_type')))) {
            for (const name of ['lock_type', 'item_composition']) {
                if (!metadata.layerCatalog[name]?.full_nest) continue;
                const entries = Object.entries(context.ownReaders.layerEntries(raw, name));
                if (!entries.length) continue;
                const children = Object.fromEntries(entries.map(([slot, entry]) => {
                    const ref = readLayerListEntryUuid(entry);
                    return [slot, fromBaseUuid(ref) ? definition(ref, []) ?? entry : entry];
                }));
                const value = metadata.layerCatalog[name]?.is_multiple
                    ? Object.keys(children).every((key, index) => key === String(index)) ? Object.values(children) : children
                    : Object.values(children)[0];
                (host.item_layer_types as Row)[name] = value;
                if (Object.prototype.hasOwnProperty.call(host, name)) host[name] = value;
            }
        }
        const rootLayers = Object.fromEntries(layerNames.filter(key => Object.prototype.hasOwnProperty.call(host, key))
            .map(key => [key, host[key]]));
        if (Object.keys(rootLayers).length) {
            const surface = constructInventoryDefinitionReferences({ items: host.items, item_layer_types: rootLayers }, references, context.rawDefinition,
                { ancestors, fullDefinition: definition });
            for (const [key, value] of Object.entries(surface.item_layer_types as Row)) host[key] = value;
        }
        host = constructInventoryDefinitionReferences(host, references, context.rawDefinition,
            { ancestors, fullDefinition: definition });
        const bucket = { ...host.item_layer_types as Row };
        for (const key of layerNames) {
            if (!Object.prototype.hasOwnProperty.call(host, key)) continue;
            if (!Object.prototype.hasOwnProperty.call(bucket, key)) bucket[key] = host[key];
            delete host[key];
        }
        host.item_layer_types = bucket;
        // Native enchantment construction writes these derived slots directly
        // into the bucket before catalog-listed roots are mirrored into it.
        const derived = ['item_charges', 'current_item_charges', 'item_rechargeable']
            .filter(key => Object.prototype.hasOwnProperty.call(bucket, key) && !metadata.definitionLayerOrder?.includes(key));
        if (fromBaseRecord(earlyEquipment)) derived.push(...Object.keys(earlyEquipment)
            .filter(key => Object.keys(context.ownReaders.layerEntries({ item_layer_types: bucket }, key)).length > 0));
        if (phpJsonTruthy(context.ownReaders.layerValue(raw, 'enchantment_template_applied'))
            && Object.prototype.hasOwnProperty.call(bucket, 'weight') && phpJsonFloat(context.ownReaders.layerValue({ item_layer_types: bucket }, 'weight')) !== 0)
            derived.unshift('weight');
        if ((readItemField(host, 'item_type') === 'creature' || context.ownReaders.layerValue(raw, 'size') === null)
            && context.ownReaders.layerValue({ item_layer_types: bucket }, 'size') !== null)
            derived.unshift('size');
        // Model donors already mirrored their own layers. Provider taxonomy
        // injected by the encounter overlay joins that prefix before loadouts,
        // so first-definition catalog interning keeps the provider's children.
        const inherited = fromBaseRecord(templateOrder) ? [...new Set([...Object.keys(templateOrder), ...deriveDefaultProviderNames(metadata.layerCatalog)])]
            .filter(key => Object.prototype.hasOwnProperty.call(bucket, key)) : [];
        host = finalizeInventoryDefinitionLayerSurface(host, metadata.definitionLayerOrder, [...inherited, ...derived], undefined, layerOrder);
        if (readItemField(host, 'item_type') === 'creature') {
            if (!postCreature) throw new FromBaseStageMissing('catalog-lean-normalization', 'post-bucket-creature-clusters');
            host = postCreature(host, ancestors.length > 0);
            const computed = host.computed as Row, creature = computed.creature as Row;
            for (const [parent, key, type] of [[computed.vitality, 'v_status', 'vitality_status'],
                [creature.combat, 'main_weapon_damage_type', 'damage_type']] as const) {
                if (!fromBaseRecord(parent)) continue;
                const ref = readItemUuidSlot(readItemField(parent, key));
                if (ref) parent[key] = constructInventoryReferenceShell(ref, type, references, context.rawDefinition) ?? parent[key];
            }
        }
        const computedOrder = readItemField(host, 'item_type') === 'creature' ? ['_cache', 'vitality', 'creature'] : [];
        const finalComputed = host.computed = Object.fromEntries([...new Set([...computedOrder, ...Object.keys(host.computed as Row)])]
            .filter(key => Object.prototype.hasOwnProperty.call(host.computed, key)).map(key => [key, (host.computed as Row)[key]]));
        const lateVitality = inheritedMaterial && readItemField(host, 'item_type') !== 'creature'
            && metadata.objectTypes.includes(String(readItemField(host, 'item_type')))
            && fromBaseRecord(finalComputed.vitality) ? finalComputed.vitality : null;
        if (lateVitality) delete finalComputed.vitality;
        const restoreVitality = lateVitality && phpJsonTruthy(context.readers.layerValue(host, 'item_composition')) ? lateVitality : null;
        const encounter = readItemField(host, 'encounter_creatures');
        const live = fromBaseRecord(encounter) && fromBaseUuid(readItemField(encounter, 'encounter_creature_id'));
        const sourceOrder = ['items', 'pictures', 'assets', 'computed', 'item_videos', 'ability_choice_options',
            ...(live ? ['encounter_creatures', 'position'] : []), 'item_layer_types'];
        const orderSource = () => Object.fromEntries([...new Set([...sourceOrder, ...Object.keys(host)])]
            .filter(key => Object.prototype.hasOwnProperty.call(host, key)).map(key => [key, host[key]]));
        host = orderSource();
        if (metadata.plSignature) finalComputed._cache = { ...finalComputed._cache as Row,
            ...compilePlSignatures(host, metadata.plSignature, signatureJson) };
        host.computed = { ...finalComputed, pl: null, pl_pending: 1, ...(restoreVitality ? { vitality: restoreVitality } : {}) };
        host = normalizeInventoryDefinition(host, metadata.layerCatalog);
        if (readItemField(host, 'item_type') === 'creature' && afterPl) host = afterPl(host, ancestors.length > 0);
        host = orderSource();
        const tableRows = (base.random_table_rows as Record<string, readonly Row[]> | undefined)?.[String(readItemField(host, 'item_id')).toUpperCase()];
        if (Array.isArray(tableRows)) host.random_table_ranges = randomTableRangesFromRows(tableRows);
        // Native nested expansions retain their full nodes until the outer
        // definition's single dedupe/intern pass has seen the complete graph.
        return ancestors.length ? host : normalizeFromBaseCatalog(host, {
            definition: id => definition(id, []), rawDefinition: id => statusOriginNonItem(id) ? null : context.rawDefinition(id), abilityTypes: metadata.abilityTypes,
            randomTableRows: base.random_table_rows as Record<string, readonly Row[] | null> | undefined });
    }
    function definition(id: string, ancestors: readonly string[]): Row | null {
        let raw: Row | null;
        try { raw = context.rawDefinition(id); }
        catch (error) {
            // A shell-only SELECT miss can close a non-item reference, but it
            // must not veto a supplied definition (including a deleted source).
            if (error instanceof FromBaseInputMissing && metadata.shellRows?.[id] === null) return null;
            throw error;
        }
        if (!raw) return null;
        const token = `${id}:${ancestors.join(',')}`;
        const cached = ancestors.length ? undefined : prepared.get(token);
        if (cached) {
            if (served.has(token)) return cached;
            const read = constructInventoryDefinitionAudio(projectDefinitionPendingCacheRead(cached), media,
                context.readers.layerValue, (row, key) => context.readers.layerValue(row, key) !== null
                    || Object.keys(context.readers.layerEntries(row, key)).length > 0);
            prepared.set(token, freezeFromBaseRow(read));
            served.add(token);
            return read;
        }
        if (active.has(token)) throw new FromBaseStageMissing('catalog-lean-normalization', `definition-cycle:${id}`);
        active.add(token);
        try {
            let prefix = context.definition(id);
            if (!prefix) return null;
            if (readItemField(prefix, 'item_type') === 'creature') {
                if (!creatureFinalizer) throw new FromBaseStageMissing('tiers-only-skills', `nested-creature:${id}`);
                prefix = creatureFinalizer(raw, prefix);
            }
            const completed = finalize(prefix, ancestors);
            freezeFromBaseRow(completed);
            if (!ancestors.length) prepared.set(token, completed);
            return completed;
        } finally { active.delete(token); }
    }
    return { finalize, definition };
}

export function compileDefinitionFinalizationEntryFromBase(base: Row, metadata: FromBaseInventoryMetadata,
    provided?: ReturnType<typeof createFromBaseDefinitionFinalizer>) {
    const result = compileClassificationEntryFromBase('definition', base, metadata);
    // Root creatures enter the same NPC finalizer as nested creature providers.
    if (!result.row || !['ability-spell-finalization', 'catalog-lean-normalization'].includes(result.missingStages[0]!)) return result;
    try {
        const finalizer = provided ?? createFromBaseDefinitionFinalizer(base, metadata);
        const id = String(readItemField(result.row, 'item_id'));
        const row = finalizer.definition(id, []);
        if (!row) throw new FromBaseInputMissing('root-definition', id);
        result.row = row;
        if (result.missingStages[0] === 'ability-spell-finalization') {
            result.completedStages.push('ability-spell-finalization'); result.missingStages.shift();
        }
        const gaps = fromBaseCatalogFinalizationGaps(base, metadata, result.row);
        if (gaps.length) result.diagnostics.push(...gaps);
        else { result.completedStages.push('catalog-lean-normalization'); result.missingStages.shift(); }
    } catch (error) {
        if (!(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
        result.diagnostics.push(error.message);
    }
    return result;
}
